/// <amd-module name="Test/TestPlansHubRefresh/Pivots/AuthorPivot/Component/AuthorTabConnectedComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { getFilteredTestCases, getTestCasesToRender, getUniqueFilterValuesToRender } from "Redux/AuthorSelector";
import {
    IAuthorPivotContextMenuParams,
    IAuthorPivotHeaderMenuParams
} from "Test/TestPlansHubRefresh/AuthorPivotMenuService/AuthorPivotMenuService.types";
import * as AuthorConstants from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/AuthorConstants";
import { AuthorActions } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorActions";
import { IColumnAndSortOrder, ITestCaseAwareState, ITestCaseItem } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorContracts";
import { getAuthorModule } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorModule";
import * as AuthorResources from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Resources";
import { getTestCaseItemsComparator } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/TestCaseItemsSortingUtility";
import { getAllColumnFields } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/DefineColumnOptionUtils";
import { PreviewSplitterOptions, SuitesConstants, TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { getIdToTestSuiteMap } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesSelector";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";
import { TestHubRefreshActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import { ITestHubRefreshLocalContext } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import {
    getDefineColumnOptionFields,
    getDefineTabCustomColumnFieldMap
} from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import * as TestHubResources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { Item, Items } from "Test/TestPlansHubRefresh/TestHub/Resources";
import { checkIfIsIE, compareColumnWidthsMaps, getDataProviderContent } from "Test/TestPlansHubRefresh/TestHub/Util";
import { TestHubRefreshFilterCommon } from "Test/TestPlansHubRefresh/TestHub/Utils/TestHubRefreshFilterCommon";
import {
    TestHubRefreshLocalContext,
    testHubRefreshLocalContextData
} from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import {
    ITestHubListBoxItemData,
    ITestPlansHubFilterItem,
    TestPlansHubFilterItemType
} from "Test/TestPlansHubRefresh/TestPlansHubRefreshFilter/TestPlansHubRefreshFilterComponent.Types";
import { TestSuiteType } from "TestPlanCommon/Contracts/TestPlan";
import { IReadonlyObservableArray, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";
import { Button } from "VSSUI/Button";
import { CustomCard } from "VSSUI/Card";
import { CustomHeader, HeaderTitle, TitleSize } from "VSSUI/Header";
import { CustomHeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { BoltListDragEvent, cellFromEvent, IListDropData, IListSelection, ListDragDropBehavior, ListDragImage, ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { ContextualMenu, IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SplitButton } from "VSSUI/SplitButton";
import { Splitter, SplitterDirection } from "VSSUI/Splitter";
import {
    ColumnMore,
    ColumnSelect,
    ColumnSorting,
    ITableColumn,
    ITableRow,
    renderSimpleCell,
    SimpleTableCell,
    sortItems,
    SortOrder,
    Table
} from "VSSUI/Table";
import { EventDispatch, IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { ZeroData } from "VSSUI/ZeroData";
import { WorkItemFormView } from "Wit/WorkItemForm/WorkItemFormView";
import { GridViewConnectedComponent } from "./GridViewConnectedComponent";
import { TestCasesTableItemProvider } from "./TestCasesTableItemProvider";

export interface IAuthorTabComponentProps extends IVssComponentProperties {
    // Id of the test plan
    planId: number;

    // Name of the test plan
    planName: string;

    // Id of the current suite
    suiteId: number;

    // Type of the current suite
    suiteType: TestSuiteType;

    // array containing the names of all the column option fields to be displayed
    columnOptionFields?: string[];

    // mapping of custom column reference names to display names
    customColumnFieldMap?: { [columnId: string]: string };

    // Title of the current suite
    suiteTitle: string;

    // Query Text of the current suite
    queryText: string;

    // Id of the initially selected suite, used during page load
    initialSelectedSuiteId: number;

    // Area path of the test plan
    areaPath: string;

    // Iteration path of the test plan
    iterationPath: string;

    // Test case items to render
    testCases: ITestCaseItem[];

    // Test case ids of the items to render, in filtering experience this array will contain what ids to render
    filteredTestCases?: number[];

    // Loading state
    isLoading: boolean;

    // indicates if paging is in progress
    isPagingInProgress: boolean;

    // indicates whether the test case preview is enabled or not
    previewStatus: string;

    // Names that show up in the AssignedTo filter
    assignedToFilterValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>;

    // State values that show up in the State filter
    stateFilterValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>;

    // The react context object. TestPlansHubRefreshConnectedView is the provider of this data.
    testhubRefreshContext?: ITestHubRefreshLocalContext;

    // This indicates wether the current view of author tab is grid or list.
    isInGridView: boolean;

    // This indicates the column name and sort order for sorting the test cases
    columnAndSortOrder: IColumnAndSortOrder;

    // map storing column id alongwith their width
    columnAndWidthMap: { [columnId: string]: number };

    // method to declare TTI
    markTTI(featureName: string, scenarioName: string, additionalProperties?: any): void;

    // handler for adding a new test case to suite
    onAddTestCaseClick(planId: number, suiteId: number, areaPath: string, iterationPath: string): void;

    // handler for adding existing test cases to suite
    onAddExistingTestCaseClick(planId: number, suiteId: number, areaPath: string): void;

    // dispatches action to load all test cases
    loadAllTestCases(planId: number, suiteId: number, continuationToken: string): void;

    // opens a test case workitem form
    openTestCase(planId: number, suiteId: number, testCaseId: number, testCaseTitle: string, testCaseState: string, testCaseAssignedTo: string): void;

    onEditQueryClick(
        areaPath: string,
        createSave: boolean,
        queryText: string,
        planId: number,
        parentSuiteId: number,
        parentSuiteTitle: string,
        rootSuiteId: number
    ): void;

    // Publishes telemetry.
    publishTelemetry(action: string, payload: { [key: string]: any }): void;

    // update column id and sort order for sorting
    updateColumnAndSortOrder(columnId: string, proposedSortOrder: SortOrder): void;

    /**
     * update test case order in case of drag drop table item
     * @param planId Id of test plan
     * @param suiteId Id of test suite
     * @param draggedtestCaseIds Ids of test cases which are being selected and dragged
     * @param startTestCaseOrder Order of the first test case in the table which is getting shifted
     * @param shiftedTestCases Ids of all the test cases which are shifted from their previous position as a result of drag drop operation
     * */
    reorderTestCases(planId: number, suiteId: number, draggedtestCaseIds: number[], startTestCaseOrder: number, shiftedTestCases: number[]): void;

    // show toast message notifications
    showToastNotifications(toastMessage: string, toastDurationMs: number): void;

    // update the column widths in state
    changeColumnWidths(columnId: string, width: number): void;

    // update the test case table after the previewed test case is edited
    updateTestCase(
        testCaseId: number,
        testCaseTitle: string,
        testCaseState: string,
        testCaseAssignedTo: string,
        columnOptions: { [id: string]: any }
    ): void;
}

/**
 * AuthorTabContentComponent: Renders the author pivot in test plans hub
 */
export class AuthorTabContentComponent extends VssComponent<IAuthorTabComponentProps, IVssComponentContext> {
    // ttiTelemetrySent flag is used to ensure we fire TTI telemetry event only once even in case where paged data is being loaded and component gets re-rendered many times
    private _ttiTelemetrySent: boolean;
    private _lastFocusedTestCaseId = new ObservableValue<number | undefined>(undefined);
    private _loadingWorkItem = new ObservableValue<boolean>(false);
    private _splitterSize: number | undefined;

    private _asyncLoadInitiated: boolean;
    private _testCasesTableSelection: IListSelection;

    private _columnSelect: ColumnSelect;
    private _context: IVssPageContext;
    private _provider: TestCasesTableItemProvider;
    private _items: ObservableArray<ITestCaseItem>;
    private _tableColumns: ITableColumn<any>[];
    // eventDispatch is required to explicitly dispatch events for table component. Events for Sorting and DragDrop Behavior were not getting dispatched intermittently
    private _eventDispatch: IEventDispatch;
    private _dragDropBehavior: ListDragDropBehavior<ITestCaseItem>;
    private _sortingBehavior: ColumnSorting<ITestCaseItem>;
    private _columnsVersionKey: number = 1;
    private _columnId: string;
    private _columnWidth: number;
    private _showLinkedItemsPanel: ObservableValue<boolean>;
    private _showCloneTestCasesPanel: ObservableValue<boolean>;

    constructor(props: IAuthorTabComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._testCasesTableSelection = new ListSelection(true);
        this._registerKeyboardShortcuts(this.props);
        this._columnSelect = new ColumnSelect();
        this._eventDispatch = new EventDispatch();
        this._context = context.pageContext;
        this._asyncLoadInitiated = false;
        this._ttiTelemetrySent = false;
        this._provider = new TestCasesTableItemProvider();
        this._showLinkedItemsPanel = new ObservableValue<boolean>(false);
        this._showCloneTestCasesPanel = new ObservableValue<boolean>(false);
        this._tableColumns = this._getColumns();
        this._onRightClick = this._onRightClick.bind(this);
        this._eventDispatch.addEventListener("contextmenu", this._onRightClick);

        this._dragDropBehavior = new ListDragDropBehavior<ITestCaseItem>({
            allowedTypes: [SuitesConstants.TestCaseDragItemType],
            id: "test-case-table",
            onDragStart: this._onDragStart,
            onDrop: this._onDrop,
            renderDragImage: (event) => {
                if (this._testCasesTableSelection.selectedCount > 1) {
                    return <ListDragImage text={this._testCasesTableSelection.selectedCount + AuthorConstants.DragTestCasesText} />;
                } else {
                    const testCaseItem = event.detail.dataTransfer.data as ITestCaseItem;
                    return <ListDragImage text={testCaseItem.name} />;
                }
            },
            type: SuitesConstants.TestCaseDragItemType
        });

        this._sortingBehavior = new ColumnSorting<ITestCaseItem>(
            (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
                if (this._tableColumns[columnIndex].sortProps) {
                    const sortFunction = getTestCaseItemsComparator(this._tableColumns[columnIndex].id, proposedSortOrder);
                    const sortFunctionsArr = sortFunction ? [sortFunction] : [null];

                    /**
                     * We pass an empty array here to take care of inverting of sort order and retain the arrows in the column header when it is sorted
                     * Actual sorting is done by the custom comparator in the sorting utility
                     **/
                    sortItems(columnIndex, proposedSortOrder, sortFunctionsArr, this._tableColumns, []);

                    this.props.updateColumnAndSortOrder(this._tableColumns[columnIndex].id, proposedSortOrder);
                }
            }
        );

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

        const splitterSizeSettings: { [key: string]: string } | undefined = settingsService.getEntry(
            TestHubRefreshConstants.PREVIEW_SPLITTER_SIZE_KEY,
            SettingsUserScope.Me
        );

        if (splitterSizeSettings && splitterSizeSettings[TestHubRefreshConstants.SETTINGS_KEY]) {
            this._splitterSize = Number(splitterSizeSettings[TestHubRefreshConstants.SETTINGS_KEY]) ?? undefined;
        }
    }

    public shouldComponentUpdate(nextProps: IAuthorTabComponentProps) {
        if (this.props.suiteId !== nextProps.suiteId) {
            this._registerKeyboardShortcuts(nextProps);
        }
        if (this.props.columnOptionFields !== nextProps.columnOptionFields) {
            this._tableColumns = this._getColumns(nextProps.columnAndWidthMap, nextProps.columnOptionFields, nextProps.customColumnFieldMap);
            // _columnsVersionKey is used as a unique key to force refresh the Table component and its behaviors, which were not being updated properly without it.
            // Causing a bug where columns could not be sorted immediately after modifying column options. Feedback ticket with ID 1605881.
            this._columnsVersionKey++;
        }

        if (!compareColumnWidthsMaps(this.props.columnAndWidthMap, nextProps.columnAndWidthMap)) {
            this._tableColumns = this._getColumns(nextProps.columnAndWidthMap, nextProps.columnOptionFields, nextProps.customColumnFieldMap);
            this._columnsVersionKey++;
        }

        this._items = this._provider.getTestCaseItems(nextProps.testCases, this._testCasesTableSelection, nextProps.filteredTestCases);

        // needed when the selected suite is changed to stop previewing the test case from previously selected suite
        if (this.props.testCases && nextProps.testCases && this.props.testCases[0] && nextProps.testCases[0]) {
            if (this.props.testCases[0].id !== nextProps.testCases[0].id) {
                this._lastFocusedTestCaseId.value = undefined;
            }
        } else {
            this._lastFocusedTestCaseId.value = undefined;
        }

        if (this.props.testCases && this._testCasesTableSelection) {
            this._registerKeyboardShortcuts(this.props);
        }

        //when the preview is enabled and non-zero test cases are loaded, select the first test case to avoid an empty preview
        if (nextProps.previewStatus === PreviewSplitterOptions.RIGHT && this._items.value.length > 0) {
            if (!this._lastFocusedTestCaseId.value) {
                this._focusFirstTestCase(nextProps.testCases);
            }
        }

        return true;
    }

    public componentWillUnmount(): void {
        const shortcutProviderService = this.context.pageContext.getService("AuthorTabKeyboardShortcutProvider") as any;
        shortcutProviderService.unregisterKeyboardShortcuts();
    }

    public render(): JSX.Element {
        let card;
        let isDynamicSuite = false;
        if (this.props.suiteType == TestSuiteType.DynamicTestSuite) {
            isDynamicSuite = true;
        }
        if (this.props.isLoading) {
            this._ttiTelemetrySent = false;
            card = <Spinner className="spinner-pos flex-grow" size={SpinnerSize.large} />;
        } else if (this.props.testCases) {
            // if filteredTestCases is valid, it's possible that testCases.length will be zero since no items match the filter. We need to render empty table in this case and not zero-data
            if (this.props.testCases.length > 0 || this.props.filteredTestCases) {
                // use wrappedComponent to load the filter bar so that it can load async
                card = <>{this._getCustomCard(this.props.isInGridView)}</>;
            } else {
                card = <>{this._getZeroDataCard(this.props.isInGridView, isDynamicSuite)}</>;
            }
        }
        const tableContent = (
            <div className="test-plan-author-tab">
                <div className="grid-view-component" style={this.props.isInGridView ? {} : { display: "none" }}>
                    <GridViewConnectedComponent />
                </div>
                <DynamicModuleLoader
                    modules={[getAuthorModule(this.props.testCases, this.props.planId, this.props.initialSelectedSuiteId, this.props.suiteId)]}
                >
                    <div className="testplans-hub-author-tab-testcase-table-container flex-grow">{card}</div>
                </DynamicModuleLoader>
                <Observer showPanel={this._showLinkedItemsPanel}>
                    {(props: { showPanel: boolean }) => {
                        return (
                            props.showPanel && (
                                <WrappedComponent
                                    dependencies={["ms.vss-test-web.testcase-linked-items"]}
                                    wrappedType="testcase-linked-items-panel"
                                    wrappedProps={{
                                        showPanel: this._showLinkedItemsPanel,
                                        testCases: this.props.testCases,
                                        selectedIndex: this._testCasesTableSelection.value[0].beginIndex,
                                        // planId required for telemetry
                                        planId: this.props.planId,
                                        updateSelection: this._updateSelection
                                    }}
                                />
                            )
                        );
                    }}
                </Observer>
                <Observer showPanel={this._showCloneTestCasesPanel}>
                    {(props: { showPanel: boolean }) => {
                        return (
                            props.showPanel && (
                                <WrappedComponent
                                    dependencies={["ms.vss-test-web.testcases-clone-panel"]}
                                    wrappedType="testcases-clone-panel"
                                    wrappedProps={{
                                        showPanel: this._showCloneTestCasesPanel,
                                        sourceTestCaseIds: this._getSelectedTestCaseIds(this._testCasesTableSelection, this.props.testCases),
                                        sourcePlanId: this.props.planId,
                                        sourcePlanName: this.props.planName,
                                        sourceSuiteId: this.props.suiteId
                                    }}
                                />
                            )
                        );
                    }}
                </Observer>
            </div>
        );
        // the preview will be enabled only when the new boards hub is enabled and when there are non-zero test points being displayed
        return isFeatureEnabled(this._context, "ms.vss-work-web.new-boards-hub-feature", false) &&
            this.props.previewStatus === PreviewSplitterOptions.RIGHT &&
            this._areNonZeroTestCasesDisplayed() ? (
            <Splitter
                className="scroll-hidden"
                farElementClassName="flex-column"
                nearElementClassName="flex-column"
                initialFixedSize={this._splitterSize ?? 320}
                onFixedSizeChanged={(newFixedSize: number) => {
                    this._onSplitterSizeChange(newFixedSize);
                }}
                onRenderNearElement={() => tableContent}
                onRenderFarElement={() => (
                    <Observer lastFocusedTestCaseId={this._lastFocusedTestCaseId} loadingWorkItem={this._loadingWorkItem}>
                        {({ lastFocusedTestCaseId, loadingWorkItem }: { lastFocusedTestCaseId: number; loadingWorkItem: boolean }) =>
                            loadingWorkItem ? (
                                <Spinner className="flex-grow" size={SpinnerSize.large} />
                            ) : lastFocusedTestCaseId ? (
                                <WorkItemFormView
                                    className="themed full-height"
                                    workItemId={lastFocusedTestCaseId}
                                    onSaveComplete={this._onSavePreview}
                                />
                            ) : null
                        }
                    </Observer>
                )}
                splitterDirection={SplitterDirection.Vertical}
            />
        ) : (
            tableContent
        );
    }

    public componentDidUpdate() {
        this._markTTIPerf();

        // This is done to make sure we call this only once at the start of page load.
        // This should only be run on intial load or when the tab is mounted with the initially selected testcases.
        // For changing of suite, we dont want this to be called
        if (!this._asyncLoadInitiated && this.props.initialSelectedSuiteId === this.props.suiteId) {
            const data = getDataProviderContent(this._context);
            if (data) {
                if (data.testCasesContinuationToken) {
                    const tokenSplits: string[] = data.testCasesContinuationToken.split(";");
                    if (tokenSplits && tokenSplits.length == 2) {
                        tokenSplits[1] = data.testCasePageSize.toString();
                        data.testCasesContinuationToken = tokenSplits.join(";");
                    }
                }

                // !This is done to ensure the same loop fetches the initial test case columns as well. The continuation token is not provided, so the initial 25 cases are also reloaded
                // !This keeps our TTI unaffected, and the extra call is not that expensive, given that it happens only in 0.06% of scenarios (when column options are used)
                let continuationTokenToUse = "";
                if (this.props.columnOptionFields) {
                    continuationTokenToUse = "";
                } else {
                    continuationTokenToUse = data.testCasesContinuationToken;
                }

                // if the json island data is dirty then we don't want to use the continuation token since we need to reload all the data
                if (testHubRefreshLocalContextData.isJsonIslandDataDirty()) {
                    continuationTokenToUse = "";
                }

                /**
                 * if json island does not contain testCases this means the page was loaded in execute tab so we need to load all test cases for this suite
                 * Or if the json island contains some test cases we still need to load all test cases (since json island will contain only 25 test cases)
                 */
                if (!data.testCases || data.testCases.length > 0) {
                    this.props.loadAllTestCases(this.props.planId, this.props.suiteId, continuationTokenToUse);
                }

                if (data.testCases) {
                    for (const testCase of data.testCases) {
                        if (testCase && testCase.workItem && !testCase.workItem.workItemFields) {
                            // Send telemetry if we see test case work items with empty fields properties.
                            const telemetryPayload = {
                                Id: testCase.workItem.id,
                                Name: testCase.workItem.name
                            };
                            this.props.publishTelemetry(TelemetryConstants.AUTHOR_PIVOT_TEST_CASE_FIELDS_MISSING, telemetryPayload);
                        }
                    }
                }
            }
        }
        this._asyncLoadInitiated = true;
    }

    public _getZeroDataCard(isInGridView: boolean, isDynamicSuite: boolean): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, AuthorConstants.AuthorTabZeroDataImagePath);
        return (
            <>
                <div className="testplans-hub-refresh-author-pivot-zerodata" style={isInGridView ? { display: "none" } : {}}>
                    <ZeroData
                        imagePath={image}
                        className="testplans-hub-author-tab-zero-data"
                        imageAltText={isDynamicSuite ? AuthorResources.EditQuery : AuthorResources.ZeroDataImageText}
                        primaryText={isDynamicSuite ? AuthorResources.ZeroDataEditQueryPrimaryText : AuthorResources.ZeroDataPrimaryText}
                        secondaryText={AuthorResources.ZeroDataSecondaryText}
                    />
                    <div className="zero-data-split-button flex-row justify-center">{this._getButtonToRender(true)}</div>
                </div>
            </>
        );
    }

    public _getCustomCard(isInGridView: boolean): JSX.Element {
        let cardContainerStyle: any = {};

        // For IE we have to explicitly set the height of the div containing the table for virtualization to work.
        if (checkIfIsIE()) {
            // 98 = 50 + 48 the height of the other sections above this one in the rhs container.
            const height = document.getElementsByClassName("testplans-hub-refresh-rhs-container")[0].clientHeight - 98;
            cardContainerStyle = { height: height + "px" };
        }

        let pagingIndicationView: JSX.Element = (<></>) as JSX.Element;
        if (this.props.isPagingInProgress) {
            // This shows three dots indicating paging
            let view = [];
            for (let i = 0; i < 3; i++) {
                view.push(<span className="testplans-paging-indication" key={i} />);
            }
            pagingIndicationView = <>{view}</>;
        }
        return (
            <>
                <div className="testplans-hub-refresh-author-pivot-table-view" style={isInGridView ? { display: "none" } : {}}>
                    <WrappedComponent
                        dependencies={["ms.vss-test-web.testplans-hub-refresh-filter-component"]}
                        wrappedType={"testplans-hub-refresh-filter-component"}
                        className={"author-pivot-filter-bar bolt-filterbar-white depth-8"}
                        filterBarItems={this._getFilterBarItems()}
                    />
                    <div className="flex-row" style={cardContainerStyle}>
                        <CustomCard
                            className="testplans-hub-author-card bolt-card-with-header"
                            headerClassName="bolt-card-header bolt-header-with-commandbar"
                        >
                            <CustomHeader className="bolt-card-header bolt-header-with-commandbar">
                                <HeaderTitle
                                    titleSize={TitleSize.Medium}
                                    className="bolt-header-title-area flex-grow scroll-hidden bolt-header-title body-xl m header-title"
                                    ariaLevel={2}
                                >
                                    {this._getCardTitle(this.props.testCases)}
                                    {pagingIndicationView}
                                </HeaderTitle>
                                <CustomHeaderCommandBar className="bolt-header-commandbar flex-noshrink bolt-header-commandbar-no-right-padding">
                                    {this._getButtonToRender(false)}
                                </CustomHeaderCommandBar>
                            </CustomHeader>
                            <Table
                                className="testplans-hub-author-tab-testcase-table"
                                containerClassName="custom-scrollbar"
                                columns={this._tableColumns}
                                eventDispatch={this._eventDispatch}
                                showLines={true}
                                scrollable={true}
                                itemProvider={this._items}
                                onSelect={this._setIfSelected}
                                singleClickActivation={false}
                                onActivate={this._openTestCaseWITForm}
                                selection={this._testCasesTableSelection}
                                behaviors={[this._sortingBehavior, this._dragDropBehavior]}
                                key={"AuthorTableVersionKey_" + this._columnsVersionKey}
                            />
                        </CustomCard>
                    </div>
                </div>
            </>
        );
    }

    public _getCardTitle(testCases: ITestCaseItem[]): string {
        let title = "";
        let itemsString: string = Items;

        if (testCases && testCases.length >= 0) {
            if (testCases.length === 1) {
                itemsString = Item;
            }

            title = AuthorResources.TestCases + ` (${testCases.length} ${itemsString})`;
        }

        return title;
    }

    private _onDragStart = (event: BoltListDragEvent<HTMLElement, ITestCaseItem>) => {
        // dragged test case is selected if there is no table selection or single test case is selected which is different from the dragged one.
        if (event.detail.dataTransfer.secondaryData && this._testCasesTableSelection.selectedCount <= 1) {
            this._testCasesTableSelection.select(event.detail.dataTransfer.secondaryData.index);
        }
        const selectionLength = this._testCasesTableSelection.value.length;
        //selected test case ids which are dragged
        const selectedtestCaseIds: number[] = [];
        for (let i = 0; i < selectionLength; i++) {
            for (let j = this._testCasesTableSelection.value[i].beginIndex; j <= this._testCasesTableSelection.value[i].endIndex; j++) {
                selectedtestCaseIds.push(this.props.testCases[j].id);
            }
        }
        // selected test case ids are stored in local context in order to fetch those ids when the test cases are dropped on test suite or in test case table.
        if (this.props.testhubRefreshContext) {
            this.props.testhubRefreshContext.setSelectedTestCaseIds(selectedtestCaseIds);
        }
    };

    private _onDrop = (event: BoltListDragEvent<HTMLElement, ITestCaseItem>, dropData: IListDropData) => {
        // If filter is set, drag drop test case is not allowed
        if (this.props.filteredTestCases && this.props.filteredTestCases.length > 0) {
            event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
            this.props.showToastNotifications(AuthorResources.ClearFilter, TestHubRefreshConstants.TOAST_MESSAGE_DURATION);
        } else {
            let isSortingEnabled: boolean = false;
            for (let i = 0; i < this._tableColumns.length; i++) {
                let sortProps = this._tableColumns[i].sortProps;
                if (sortProps && sortProps.sortOrder !== undefined) {
                    if (this._tableColumns[i] && this._tableColumns[i].id === "order" && sortProps.sortOrder === 0) {
                        break;
                    } else {
                        isSortingEnabled = true;
                        break;
                    }
                }
            }
            // if sorting is enabled, drag drop test case is not allowed
            if (isSortingEnabled) {
                event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
                this.props.showToastNotifications(AuthorResources.SortByOrderAscending, TestHubRefreshConstants.TOAST_MESSAGE_DURATION);
            } else {
                let selectedtestCaseIds: number[] = [];
                if (this.props.testhubRefreshContext) {
                    selectedtestCaseIds = this.props.testhubRefreshContext.getSelectedTestCaseIds();
                }
                //test cases which will be shifted from their original position once the selected test cases are dropped
                const shiftedTestCases = [];
                const selectionLength = this._testCasesTableSelection.value.length;
                // shiftStartIndex - Index of the first test case from where the test cases will be shifted on drop
                let shiftStartIndex = this._testCasesTableSelection.value[0].beginIndex;
                // shiftEndIndex - Index of the last test case upto where the test cases will be shifted on drop
                let shiftEndIndex = this._testCasesTableSelection.value[selectionLength - 1].endIndex;
                if (dropData.index < shiftStartIndex) {
                    shiftStartIndex = dropData.index;
                }

                if (dropData.index > shiftEndIndex) {
                    shiftEndIndex = dropData.index;
                }
                // startTestCaseOrder - order of the first test case which will be shifted
                const startTestCaseOrder = this.props.testCases[shiftStartIndex].order;
                for (let i = shiftStartIndex; i <= shiftEndIndex; i++) {
                    if (this.props.testCases[i]) {
                        shiftedTestCases.push(this.props.testCases[i].id);
                    }
                }

                // effectiveDropIndex - Index in shiftedTestCases array where test cases are dropped
                const effectiveDropIndex = dropData.index - shiftStartIndex;
                for (let i = 0; i < selectedtestCaseIds.length; i++) {
                    const testCaseId = selectedtestCaseIds[i];
                    const testCaseIndex = shiftedTestCases.indexOf(testCaseId);
                    /**
                     * New entry is added for the dragged test cases in shiftedTestCases array. So all the older entries of dragged test cases
                     * are marked as negative. Older entries are not deleted because it will alter the effectiveDropIndex
                     */
                    if (shiftedTestCases[testCaseIndex] > 0) {
                        shiftedTestCases[testCaseIndex] *= -1;
                    }
                    // selected test cases are shifted to the target location
                    shiftedTestCases.splice(effectiveDropIndex + i, 0, testCaseId);
                }
                this.props.reorderTestCases(this.props.planId, this.props.suiteId, selectedtestCaseIds, startTestCaseOrder, shiftedTestCases);
                this._testCasesTableSelection.clear();
            }
        }
    };

    private _getButtonToRender(isPrimary: boolean): JSX.Element {
        let isDynamicSuite = false;
        if (this.props.suiteType == TestSuiteType.DynamicTestSuite) {
            isDynamicSuite = true;
        }
        return isDynamicSuite ? (
            <Button text={AuthorResources.EditQuery} primary={isPrimary} onClick={this._editQuery} />
        ) : (
            <SplitButton
                primary={isPrimary}
                buttonProps={{
                    text: AuthorResources.NewTestCase,
                    onClick: () => {
                        this._addTestCase();
                    },
                    tooltipProps: { text: AuthorResources.AddNewTestCaseUsingForm }
                }}
                menuButtonProps={{
                    ariaLabel: AuthorResources.AddTestCaseMenuButtonAriaLabel,
                    contextualMenuProps: {
                        menuProps: {
                            id: "add-existing-test-case-menu",
                            items: this._getSubMenuItemsForAddingTestCase()
                        }
                    }
                }}
            />
        );
    }

    /**
     * _getFilterBarItems: Returns the filter bar items and the values to render within them in case of dropdown filter item
     */
    private _getFilterBarItems(): ITestPlansHubFilterItem[] {
        let filterBarItems: ITestPlansHubFilterItem[];

        const textFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.TextFilter,
            key: "author-filter-text",
            filterItemKey: TestHubRefreshConstants.AUTHOR_TEXT_FILTER_ITEM_KEY,
            placeholder: AuthorResources.TextFilterPlaceholderText,
            items: {} as ObservableArray<IListBoxItem<ITestHubListBoxItemData>>
        };

        // assignedTo and state filter use objects from TestHubRefreshFilterCommon for items and selection so that we are able to persist their values across tab switches
        const assignedToDropdownFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.DropDownFilter,
            key: "author-assignedto-filter",
            filterItemKey: TestHubRefreshConstants.AUTHOR_ASSIGNEDTO_FILTER_ITEM_KEY,
            placeholder: AuthorResources.AssignedToFilterPlaceholderText,
            items: this._getDropdownItems(TestHubRefreshFilterCommon.assignedToFilterValues, this.props.assignedToFilterValues),
            selection: TestHubRefreshFilterCommon.assignedToFilterSelection,
            width: 220
        };

        const stateDropdownFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.DropDownFilter,
            key: "author-state-filter",
            filterItemKey: TestHubRefreshConstants.AUTHOR_STATE_FILTER_ITEM_KEY,
            placeholder: AuthorResources.StateFilterPlaceholderText,
            items: this._getDropdownItems(TestHubRefreshFilterCommon.stateFilterValues, this.props.stateFilterValues),
            selection: TestHubRefreshFilterCommon.stateFilterSelection,
            width: 160
        };

        filterBarItems = [textFilterItem, assignedToDropdownFilterItem, stateDropdownFilterItem];

        return filterBarItems;
    }

    /**
     * This method takes the class member object and the props object and merges the two and returns the dropdown filter items
     * This is required to maintain the filter context as user switches between suites. The new items in props come in as data is fetched via paging.
     */
    private _getDropdownItems(
        existingItems: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>,
        newItems: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>
    ): ObservableArray<IListBoxItem<ITestHubListBoxItemData>> {
        if (existingItems && newItems) {
            for (const newItem of newItems.value) {
                if (existingItems.value.findIndex((value) => value.text === newItem.text) === -1) {
                    existingItems.push(newItem);
                }
            }
        }

        return existingItems;
    }

    private _addTestCase = (): void => {
        this.props.onAddTestCaseClick(this.props.planId, this.props.suiteId, this.props.areaPath, this.props.iterationPath);
    };

    private _editQuery = (): void => {
        this.props.onEditQueryClick(
            this.props.areaPath,
            false,
            this.props.queryText,
            this.props.planId,
            this.props.suiteId,
            this.props.suiteTitle,
            0
        );
    };

    private _openTestCaseWITForm = (): void => {
        const testCaseIndex = this._testCasesTableSelection.value[0].beginIndex;
        const testCaseId: number = this.props.testCases[testCaseIndex].id;
        const testCaseTitle: string = this.props.testCases[testCaseIndex].name;
        const testCaseState: string = this.props.testCases[testCaseIndex].state;
        const testCaseAssignedTo: string = this.props.testCases[testCaseIndex].assignedTo.displayName;
        this.props.openTestCase(this.props.planId, this.props.suiteId, testCaseId, testCaseTitle, testCaseState, testCaseAssignedTo);
    };

    // Get Contextual menu items for the add test case
    private _getSubMenuItemsForAddingTestCase = (): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        const props: IAuthorPivotHeaderMenuParams = {
            planId: this.props.planId,
            planName: this.props.plan,
            suiteId: this.props.suiteId,
            suiteType: this.props.suiteType,
            areaPath: this.props.areaPath,
            iterationPath: this.props.iterationPath,
            dispatch: this.props.dispatch
        };
        menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.add-test-case-options-menu", props));

        return menuItems;
    };

    private _markTTIPerf() {
        if (this.props.testCases && this.props.testhubRefreshContext) {
            if (this.props.testhubRefreshContext.isPageLoad()) {
                // This is a page load scenario, use helper action to declare TTI
                this.props.markTTI(TelemetryConstants.TEST_HUB_FEATURE_AREA_NAME, TelemetryConstants.AUTHOR_PIVOT_PAGE_LOAD_TTI_SCENARIO, {
                    ttiScenario: "PageLoad"
                });
                this.props.testhubRefreshContext.setPageLoad(false);
                this._ttiTelemetrySent = true;
            } else {
                if (this.props.isLoading === false && this._ttiTelemetrySent === false) {
                    // This is either a tab switch (from execute -> define for e.g.) or a suite selection changed scenario
                    const performanceSerivce: IVssPerformanceService =
                        this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

                    // declare TTI only if the scenario was already started (i.e. it is currently active). For suite change, the scenario would have been started in Suite connected component
                    if (performanceSerivce.getActiveScenario(TelemetryConstants.AUTHOR_PIVOT_PAGE_LOAD_TTI_SCENARIO)) {
                        performanceSerivce.endScenario(
                            TelemetryConstants.TEST_HUB_FEATURE_AREA_NAME,
                            TelemetryConstants.AUTHOR_PIVOT_PAGE_LOAD_TTI_SCENARIO,
                            true
                        );
                        this._ttiTelemetrySent = true;
                    }
                }
            }
        }
    }

    private _getTestCaseContextMenuItemsFromProvider = (): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        const props: IAuthorPivotContextMenuParams = {
            selection: this._testCasesTableSelection,
            planId: this.props.planId,
            planName: this.props.planName,
            suiteId: this.props.suiteId,
            suiteName: this.props.suiteTitle,
            testCases: this.props.testCases,
            suiteType: this.props.suiteType,
            areaPath: this.props.areaPath,
            iterationPath: this.props.iterationPath,
            dispatch: this.props.dispatch,
            linkedItemsPanelOpen: this._openLinkedItemsPanel,
            cloneTestCasesPanelOpen: this._openCloneTestCasesPanel
        };
        menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.test-case-context-menu", props));
        return menuItems;
    };

    private _onColumnSize = (event: MouseEvent, columnIndex: number, width: number) => {
        const originalColumnWidth = this._tableColumns[columnIndex].width;
        (originalColumnWidth as ObservableValue<number>).value = width;
        this._columnId = this._tableColumns[columnIndex].id;
        this._columnWidth = width;
    };

    private _columnSizeEnd = () => {
        this.props.changeColumnWidths(this._columnId, this._columnWidth);
        this.props.saveColumnWidths(this.props.columnAndWidthMap);
    };

    private _getColumns(
        columnAndWidthMap?: { [columnId: string]: number },
        columnOptionFields?: string[],
        customColumnFieldMap?: { [columnId: string]: string }
    ): ITableColumn<any>[] {
        const tableColumns: ITableColumn<any>[] = [];
        const menuItemColumn = new ColumnMore(
            () => {
                return {
                    id: "test-case-context-menu-column",
                    items: this._getTestCaseContextMenuItemsFromProvider()
                };
            },
            undefined,
            (rowindex: number, columnIndex: number) => {
                if (!this._testCasesTableSelection.selected(rowindex)) {
                    this._testCasesTableSelection.select(rowindex, 1, false);
                }
            }
        );

        tableColumns.push(
            this._columnSelect,
            {
                id: AuthorConstants.NameColumn,
                name: AuthorResources.Title,
                width: this._getWidth(500, AuthorConstants.NameColumn, columnAndWidthMap),
                minWidth: 10,
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell,
                sortProps:
                    this.props.columnAndSortOrder && this.props.columnAndSortOrder.columnId === AuthorConstants.NameColumn
                        ? { sortOrder: this.props.columnAndSortOrder.sortOrder }
                        : {}
            },
            menuItemColumn,
            {
                id: AuthorConstants.OrderColumn,
                name: AuthorResources.Order,
                width: this._getWidth(70, AuthorConstants.OrderColumn, columnAndWidthMap),
                minWidth: 10,
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell,
                sortProps:
                    this.props.columnAndSortOrder && this.props.columnAndSortOrder.columnId === AuthorConstants.OrderColumn
                        ? { sortOrder: this.props.columnAndSortOrder.sortOrder }
                        : {}
            }
        );

        if (columnOptionFields) {
            const fields = getAllColumnFields(columnOptionFields, customColumnFieldMap);
            for (const columnOptionField of fields) {
                tableColumns.push({
                    id: columnOptionField.id,
                    name: columnOptionField.name,
                    width: this._getWidth(100, columnOptionField.id, columnAndWidthMap),
                    minWidth: 10,
                    onSize: this._onColumnSize,
                    onSizeEnd: this._columnSizeEnd,
                    renderCell:
                        columnOptionField.id === AuthorConstants.WorkItemCreatedDate || columnOptionField.id === AuthorConstants.WorkItemChangedDate
                            ? (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<any>, tableItem: any) => {
                                //Convert to string before rendering, this is not done in columnOptionsCommonUtils to facilitate sorting
                                if (tableItem.columnOptionValues[columnOptionField.id]) {
                                    return (
                                        <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                                            {tableItem.columnOptionValues[columnOptionField.id].toLocaleString()}
                                        </SimpleTableCell>
                                    );
                                } else {
                                    return <div></div>;
                                }
                            }
                            : renderSimpleCell,
                    className: "body-m",
                    sortProps:
                        this.props.columnAndSortOrder && this.props.columnAndSortOrder.columnId === columnOptionField.id
                            ? { sortOrder: this.props.columnAndSortOrder.sortOrder }
                            : {}
                });
            }
        }

        return tableColumns;
    }

    private _onRightClick(event: React.MouseEvent<HTMLElement>) {
        if (event.button === TestHubRefreshConstants.RIGHT_MOUSE_BUTTON) {
            event.preventDefault();

            const { cellElement, rowIndex } = cellFromEvent(event);

            if (rowIndex >= 0) {
                if (!this._testCasesTableSelection.selected(rowIndex)) {
                    this._testCasesTableSelection.select(rowIndex, 1, false);
                }

                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    return React.createElement(
                        ContextualMenu,
                        {
                            menuProps: {
                                id: "test-case-context-menu-column",
                                items: this._getTestCaseContextMenuItemsFromProvider()
                            },
                            anchorElement: event.currentTarget as HTMLElement,
                            anchorPoint: { x: event.pageX, y: event.pageY },
                            onDismiss: onDismiss
                        },
                        null
                    );
                });
            }
        }
    }

    private _getWidth(
        defaultWidth: number,
        columnId: string,
        columnAndWidthMap: { [columnId: string]: number } | undefined
    ): ObservableValue<number> {
        return columnAndWidthMap && columnAndWidthMap[columnId] !== undefined
            ? new ObservableValue(columnAndWidthMap[columnId])
            : new ObservableValue(defaultWidth);
    }

    private _registerKeyboardShortcuts(props: IAuthorTabComponentProps): void {
        const params: IAuthorPivotContextMenuParams = {
            planId: props.planId,
            planName: props.planName,
            suiteId: props.suiteId,
            suiteType: props.suiteType,
            areaPath: props.areaPath,
            iterationPath: props.iterationPath,
            dispatch: props.dispatch,
            suiteName: props.suiteTitle,
            testCases: this.props.testCases,
            selection: this._testCasesTableSelection
        };
        const contributionService: IVssContributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getContributionAsync("ms.vss-test-web.keyboard-shortcuts-module").then(() => {
            const shortcutProviderService = this.context.pageContext.getService("AuthorTabKeyboardShortcutProvider") as any;
            shortcutProviderService.registerKeyboardShortcuts(params);
        });
    }

    private _getSelectedTestCaseIds(testCasesTableSelection: IListSelection, testCases: ITestCaseItem[]): number[] {
        let selectedtestCaseIds: number[] = [];
        const selectionLength = testCasesTableSelection.value.length;
        for (let i = 0; i < selectionLength; i++) {
            for (let j = this._testCasesTableSelection.value[i].beginIndex; j <= testCasesTableSelection.value[i].endIndex; j++) {
                selectedtestCaseIds.push(testCases[j].id);
            }
        }
        return selectedtestCaseIds;
    }

    public _openLinkedItemsPanel = () => {
        this._showLinkedItemsPanel.value = true;
    };

    /*
    Called when test cases are switched in test case linked items panel to reflect the change in selection in the test case table
    ** @next - true when the next test case is to be selected and false when the previous test case is to be selected
    */
    public _updateSelection = (next: boolean) => {
        const selectedIndex: number = this._testCasesTableSelection.value[0].beginIndex;
        if (next) {
            if (this.props.testCases.length === selectedIndex + 1) {
                this._testCasesTableSelection.select(0);
            } else {
                this._testCasesTableSelection.select(selectedIndex + 1);
            }
        } else {
            if (selectedIndex === 0) {
                this._testCasesTableSelection.select(this.props.testCases.length - 1);
            } else {
                this._testCasesTableSelection.select(selectedIndex - 1);
            }
        }
    };

    public _openCloneTestCasesPanel = () => {
        this._showCloneTestCasesPanel.value = true;
    };

    private _focusFirstTestCase = (testCases: any) => {
        this._testCasesTableSelection.select(0);

        let selectedTestCase;
        if (testCases) {
            selectedTestCase = testCases[0];
        }

        if (selectedTestCase) {
            this._onFocusItem(selectedTestCase.id);
        }
    };

    private _setIfSelected = (event: React.MouseEvent<HTMLElement>, listRow: ITableRow<any>) => {
        if (listRow) {
            const { data, index } = listRow;
            this._onFocusItem(data[TestHubRefreshConstants.WorkItemField_Id]);
        }
    };

    private _onFocusItem = async (testCaseId: number) => {
        // called when the test cases props are loaded inside the component and preview feature is on
        if (this._lastFocusedTestCaseId.value !== testCaseId) {
            this._lastFocusedTestCaseId.value = testCaseId;
        }
    };

    private _areNonZeroTestCasesDisplayed = (): boolean => {
        if ((this.props.testCases && this.props.testCases.length > 0) || (this.props.filteredTestCases && this.props.filteredTestCases.length > 0)) {
            return true;
        }
        return false;
    };

    private _onSplitterSizeChange = (value: number) => {
        this._splitterSize = value;
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = {
            [`${TestHubRefreshConstants.PREVIEW_SPLITTER_SIZE_KEY}/${TestHubRefreshConstants.SETTINGS_KEY}`]: value
        };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    };

    private _getIdentityDisplayString(identityDisplayName: string) {
        let displayName: string = TestHubResources.UnassignedString;

        if (!identityDisplayName || identityDisplayName.trim().length <= 0) {
            return displayName;
        }

        displayName = identityDisplayName;

        const index: number = identityDisplayName.indexOf("<");
        if (identityDisplayName && index > 0) {
            displayName = identityDisplayName.substr(0, index - 1);
        }

        return displayName;
    }

    private _onSavePreview = (workItem: any) => {
        const newTestCaseTitle = workItem.getTitle();
        const newTestCaseState = workItem.getFieldValueByName(TestHubRefreshConstants.WorkItemField_AssignedTo);

        let newTestCaseAssignedTo = workItem.getFieldValueByName(TestHubRefreshConstants.WorkItemField_AssignedTo);
        newTestCaseAssignedTo = this._getIdentityDisplayString(newTestCaseAssignedTo);

        const columnOptions: { [id: string]: any } = {};
        if (this.props.columnOptionFields) {
            for (const columnOptionsField of this.props.columnOptionFields) {
                columnOptions[columnOptionsField] = workItem.getFieldValueByName(columnOptionsField);
            }
        }

        this.props.updateTestCase(workItem.id, newTestCaseTitle, newTestCaseState, newTestCaseAssignedTo, columnOptions);
    };
}

function mapStateToProps(state: ITestCaseAwareState): Partial<IAuthorTabComponentProps> {
    let testCases;
    let filteredTestCases;
    let assignedToFilterValues, stateFilterValues;
    let suiteMap = new Map();
    let suiteType;
    let queryText;
    let suiteTitle;
    let columnOptionFields;
    let customColumnFieldMap;

    if (state.authorState && state.authorState.testCases) {
        testCases = getTestCasesToRender(state);
        filteredTestCases = getFilteredTestCases(state);
        columnOptionFields = getDefineColumnOptionFields(state);
        customColumnFieldMap = getDefineTabCustomColumnFieldMap(state);

        const filterValuesWrapper = getUniqueFilterValuesToRender(state);
        assignedToFilterValues = filterValuesWrapper[TestHubRefreshConstants.AUTHOR_ASSIGNEDTO_FILTER_ITEM_KEY];
        stateFilterValues = filterValuesWrapper[TestHubRefreshConstants.AUTHOR_STATE_FILTER_ITEM_KEY];
    }

    if (state.suiteState) {
        suiteMap = getIdToTestSuiteMap(state);
        const selectedSuite = suiteMap.get(state.testHubRefreshState.selectedSuiteId);
        if (selectedSuite) {
            suiteType = selectedSuite.suiteType;
            queryText = selectedSuite.queryString;
            suiteTitle = selectedSuite.name;
        }
    }

    return {
        planId: state.testHubRefreshState.testPlanId,
        planName: state.testHubRefreshState.testPlanName,
        areaPath: state.testHubRefreshState.testPlanAreaPath,
        columnOptionFields: columnOptionFields,
        customColumnFieldMap: customColumnFieldMap,
        iterationPath: state.testHubRefreshState.testPlanIteration,
        suiteId: state.testHubRefreshState.selectedSuiteId,
        suiteType: suiteType,
        queryText: queryText,
        suiteTitle: suiteTitle,
        initialSelectedSuiteId: state.testHubRefreshState.initialSelectedSuiteId,
        isLoading: state.testHubRefreshState.isLoading,
        testCases: testCases,
        filteredTestCases: filteredTestCases,
        assignedToFilterValues: assignedToFilterValues,
        stateFilterValues: stateFilterValues,
        isInGridView: state.testHubRefreshState.isInGridView,
        isPagingInProgress: state.testHubRefreshState.isPagingInProgress,
        columnAndWidthMap: state.testHubRefreshState.defineColumnAndWidthMap,
        columnAndSortOrder: state.authorState && state.authorState.columnAndSortOrder ? state.authorState.columnAndSortOrder : undefined,
        previewStatus: state.testHubRefreshState.testCasePreviewStatus
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<IAuthorTabComponentProps> {
    return {
        markTTI: (featureName: string, scenarioName: string, additionalProperties?: any) =>
            dispatch(TTIMonitorActions.markTTI(featureName, scenarioName, additionalProperties)),
        onAddTestCaseClick: (planId, suiteId, areaPath, iterationPath) =>
            dispatch(AuthorActions.openNewTestCaseWitForm(planId, suiteId, areaPath, iterationPath)),
        onAddExistingTestCaseClick: (planId, suiteId, areaPath) => dispatch(AuthorActions.addExistingTestCases(planId, suiteId, areaPath)),
        openTestCase: (planId, suiteId, testCaseId, testCaseTitle, /*testCaseIndex,*/ testCaseState, testCaseAssignedTo) =>
            dispatch(AuthorActions.openExistingTestCaseWitForm(planId, suiteId, testCaseId, testCaseTitle, testCaseState, testCaseAssignedTo)),
        loadAllTestCases: (planId, suiteId, continuationToken) =>
            dispatch(AuthorActions.loadTestCasesForSelectedSuite(planId, suiteId, continuationToken)),
        onEditQueryClick: (
            areaPath: string,
            createSave: boolean,
            queryText: string,
            planId: number,
            parentSuiteId: number,
            parentSuiteTitle: string,
            rootSuiteId: number
        ) =>
            dispatch(
                SuitesActions.openCreateEditSuiteQueryForm(
                    areaPath,
                    createSave,
                    queryText,
                    planId,
                    parentSuiteId,
                    parentSuiteTitle,
                    rootSuiteId,
                    undefined
                )
            ),
        publishTelemetry: (action: string, payload: { [key: string]: any }) => dispatch(TelemetryActions.publishTelemetry(action, payload)),
        updateColumnAndSortOrder: (columnId: string, proposedSortOrder: SortOrder) =>
            dispatch(AuthorActions.updateColumnAndSortOrder(columnId, proposedSortOrder)),
        reorderTestCases: (planId: number, suiteId: number, draggedtestCaseIds: number[], startTestCaseOrder: number, shiftedTestCaseIds: number[]) =>
            dispatch(AuthorActions.reorderTestCases(planId, suiteId, draggedtestCaseIds, startTestCaseOrder, shiftedTestCaseIds)),
        showToastNotifications: (toastMessage: string, toastDurationMs: number) =>
            dispatch(SuitesActions.showToastNotifications(toastMessage, toastDurationMs)),
        changeColumnWidths: (columnId: string, width: number) => dispatch(TestHubRefreshActions.changeDefineColumnWidths(columnId, width, undefined)),
        saveColumnWidths: (columnAndWidthMap: { [columnId: string]: number }) => dispatch(AuthorActions.saveColumnWidths(columnAndWidthMap)),
        updateTestCase: (
            testCaseId: number,
            testCaseTitle: string,
            testCaseState: string,
            testCaseAssignedTo: string,
            columnOptions: { [id: string]: any }
        ) => dispatch(AuthorActions.updateTestCase(testCaseId, testCaseTitle, testCaseState, testCaseAssignedTo, columnOptions)),
        dispatch
    };
}

/**
 * AuthorTabFunctionalComponent: This functional component is just a dumb wrapper over AuthorTabContentComponent.
 * We use this because React.useContext can only be invoked in function components. We get the context here and then pass is as props to the class component.
 */
export function AuthorTabFunctionalComponent(props: IAuthorTabComponentProps): JSX.Element {
    const testhubRefreshLocalContext: ITestHubRefreshLocalContext = React.useContext(TestHubRefreshLocalContext);

    return <AuthorTabContentComponent {...props} testhubRefreshContext={testhubRefreshLocalContext} />;
}

export const AuthorTabConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(AuthorTabFunctionalComponent);

VssComponent.register("authorPivot", AuthorTabConnectedComponent as ((props: IAuthorTabComponentProps) => JSX.Element));
